import { Injectable } from '@angular/core';
import { FormRepository } from '@ieCore/repositories/form.repository';
import {
  requestLoading,
  RequestLoadingType,
} from '@ieCore/helpers/request-loading.helper';
import {
  ActivityFormResponseDto,
  FormResponseDto,
} from 'shared-general-libs/dto/form';
import { FrameworkTags } from 'shared-general-libs/enum/framework';
import {
  PhoneCodeResponseDto,
  RegionResponseDto,
} from 'shared-general-libs/dto/region';
import { ReportTemplate } from 'shared-general-libs/type/report';
import { OrganizationService } from '@ieCore/services/organization.service';
import { combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { FormDefaultValueTypes } from 'shared-general-libs/enum/form';

@Injectable({
  providedIn: 'root',
})
export class FormService {
  constructor(
    private formRepository: FormRepository,
    private organizationService: OrganizationService,
  ) {}

  /**
   * Item 6: merges the caller org's `activityFormDefaults[activity]` into
   * the served form config as `defaultValue: { type: CONST, value }` per
   * field key — WITHOUT overriding a `disabled` field or a REQUEST/
   * FORM_STORE default (see orgDefaultMayOverride). Returns the SAME
   * `formConfig` object reference (byte-identical) in EVERY case where
   * nothing actually changes: an absent/empty org config, a config whose
   * keys match no field, or a config whose only matching field(s) are not
   * eligible for override — not just the absent-config case. An org-fetch
   * failure also resolves to `{}` at the OrganizationService layer, so it
   * falls into the same untouched path.
   *
   * Single choke point: every caller of getFormByActivityName (ie-manage-
   * activity, accounting-consolidated-details, activity-partner-tree) is
   * covered automatically.
   */
  private static mergeActivityFormDefaults(
    formConfig: ActivityFormResponseDto,
    defaults: Record<string, string> | undefined,
  ): ActivityFormResponseDto {
    if (!defaults || !Object.keys(defaults).length) {
      return formConfig;
    }

    let changed = false;
    const mergedForm = formConfig.form.map((field) => {
      const value = defaults[field.key];
      if (value === undefined || !FormService.orgDefaultMayOverride(field)) {
        return field;
      }
      changed = true;
      return {
        ...field,
        defaultValue: { type: FormDefaultValueTypes.CONST, value },
      };
    });

    if (!changed) {
      return formConfig;
    }

    return { ...formConfig, form: mergedForm };
  }

  /**
   * Spec A §4.1 / plan deviation 2. An org activityFormDefaults value is MORE SPECIFIC
   * than a platform CONST default, so it wins — otherwise MRF's
   * `AIR_EMISSIONS_ACTIVITY: { entryMethod: 'STACK_MEASUREMENT' }` would be dead config
   * (the form ships a CONST default of DIRECT_QUANTITY so new records preselect today's
   * behaviour for every other tenant and legacy records open as DIRECT on edit).
   *
   * Two defaults are still untouchable, by construction rather than by allowlist:
   *  - a `disabled` field: its CONST default IS the value (production-data `unit` = MT).
   *    An admin typo must never silently change a locked platform constant.
   *  - a REQUEST / FORM_STORE default: those resolve at runtime from a backend call or
   *    the form store, and a literal string could not stand in for either.
   */
  private static orgDefaultMayOverride(field: FormResponseDto): boolean {
    if (field.disabled) {
      return false;
    }
    return (
      !field.defaultValue ||
      field.defaultValue.type === FormDefaultValueTypes.CONST
    );
  }

  getFormByName(
    name: string,
    payload: unknown = {},
  ): RequestLoadingType<FormResponseDto[]> {
    return requestLoading(this.formRepository.getFormByName(name, payload));
  }

  getReportTemplate(id: string): RequestLoadingType<ReportTemplate> {
    return requestLoading(this.formRepository.getReportTemplate(id));
  }

  getFormByActivityName(
    activity: string,
    frameworkTags?: FrameworkTags[],
  ): RequestLoadingType<ActivityFormResponseDto> {
    return requestLoading(
      combineLatest([
        this.formRepository.getFormByActivityName(activity, frameworkTags),
        // R9: getActivityFormDefaults() maps the LIVE, never-completing
        // orgConfig$ stream (by design — see its doc comment). Combined
        // with the one-shot form HTTP above without take(1), the pair
        // would never complete (finalize() never clears isLoading) and
        // would replay a STALE form over a later activity's formConfig
        // whenever selectUserOrgContext next emits. Snapshot per fetch.
        this.organizationService.getActivityFormDefaults().pipe(take(1)),
      ]).pipe(
        map(([formConfig, activityFormDefaults]) =>
          FormService.mergeActivityFormDefaults(
            formConfig,
            activityFormDefaults[activity],
          ),
        ),
      ),
    );
  }

  getRegions(lang: string): RequestLoadingType<RegionResponseDto[]> {
    return requestLoading(this.formRepository.getRegions(lang));
  }

  getPhoneCodes(): RequestLoadingType<PhoneCodeResponseDto[]> {
    return requestLoading(this.formRepository.getPhoneCodes());
  }
}
